import { isAxiosError } from "axios";

/**
 * Client side of lookbook passwords and guest engagement.
 *
 * A password-protected lookbook is unlocked once per browser: the server trades
 * the password for an access token, kept here per lookbook and sent on every
 * public lookbook request as X-Lookbook-Token. Changing the password on the
 * server invalidates the token, and the next request simply asks again.
 *
 * Guests — visitors who aren't signed in, on a lookbook whose owner allows them
 * to vote and comment — are identified by a random id this browser keeps, and
 * the name they last commented under is remembered for the next comment.
 */

const TOKEN_KEY_PREFIX = "lookbook-access:";
const VISITOR_ID_KEY = "lookbook-visitor-id";
const GUEST_NAME_KEY = "lookbook-guest-name";

export const PASSWORD_REQUIRED_CODE = "LOOKBOOK_PASSWORD_REQUIRED";

// Storage can throw (private mode, quota, disabled cookies). Every caller here
// degrades to "not remembered" rather than failing the page.
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Not remembered — the visitor is asked again next time.
  }
}

export function getLookbookToken(publicId: string): string | null {
  return read(TOKEN_KEY_PREFIX + publicId);
}

export function setLookbookToken(publicId: string, token: string | null): void {
  write(TOKEN_KEY_PREFIX + publicId, token);
}

/** Headers for a public lookbook request — empty for every other board. */
export function lookbookAccessHeaders(
  publicId?: string
): Record<string, string> {
  if (!publicId) return {};
  const token = getLookbookToken(publicId);
  return token ? { "X-Lookbook-Token": token } : {};
}

/** Whether a failed lookbook request means "ask for the password". */
export function isLookbookPasswordRequired(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 403 &&
    (error.response?.data as { code?: string } | undefined)?.code ===
      PASSWORD_REQUIRED_CODE
  );
}

// Held in memory too, so a browser that won't store it still keeps one id for
// the life of the page. Without this, every call minted a new id: the votes
// query key changed on each render and a second click voted again instead of
// taking the first vote back.
let visitorId: string | null = null;

export function getVisitorId(): string {
  if (visitorId) return visitorId;
  visitorId = read(VISITOR_ID_KEY) ?? crypto.randomUUID();
  write(VISITOR_ID_KEY, visitorId);
  return visitorId;
}

export function getGuestName(): string {
  return read(GUEST_NAME_KEY) ?? "";
}

export function setGuestName(name: string): void {
  write(GUEST_NAME_KEY, name.trim() || null);
}
